import {useState} from "react";
import {Card} from "../../../basic/card.tsx";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {CreateTeamDialog} from "../../../dialogs/admin/CreateTeamDialog";
import {TeamDetailsDialog} from "../../../dialogs/admin/TeamDetailsDialog";
import {PaginationControls} from "../../../shared/Pagination.tsx";
import {SearchInput} from "../../../shared/search/SearchInput.tsx";
import {TeamTable} from "./TeamTable.tsx";
import {TeamActions} from "./TeamActions.tsx";
import {useGetTeamByIdQuery, useGetTeamsQuery} from "../../../../store/generated/api.ts";

export function AdminTeamsView() {
    const {t} = useTranslationWithKeys();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [teamDialogOpen, setTeamDialogOpen] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
    const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);

    const {
        data: teamsData,
        isLoading,
        error
    } = useGetTeamsQuery({
        page: currentPage,
        size: pageSize,
        ...(searchTerm && {search: searchTerm})
    });

    const teams = teamsData?.teams || [];
    const pagination = teamsData?.pagination;

    const {
        data: selectedTeamData,
        isLoading: isTeamLoading,
        error: teamError
    } = useGetTeamByIdQuery(
        {teamId: selectedTeamId || ''},
        {skip: !selectedTeamId}
    );

    const handleTeamClick = (teamId: string) => {
        setSelectedTeamId(teamId);
        setTeamDialogOpen(true);
    };

    const handleCloseTeamDialog = () => {
        setTeamDialogOpen(false);
        setSelectedTeamId(null);
    };

    const handleCreateTeam = () => {
        setCreateTeamDialogOpen(true);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page - 1);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(0);
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(0);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.teams.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.teams.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-32">
                            <div className="text-muted-foreground">{t('common.loading')}</div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h1>{t('admin.teams.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('admin.teams.description')}</p>
                </div>
                <Card className="shadow-sm border-border/50">
                    <div className="p-6">
                        <div className="flex items-center justify-center h-32">
                            <div className="text-destructive">
                                {t('admin.teams.error.loadingFailed')}
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('admin.teams.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('admin.teams.description')}</p>
            </div>

            <Card className="shadow-sm border-border/50">
                <div className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <SearchInput
                                placeholder={t('admin.teams.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <TeamActions
                                totalTeams={pagination?.total || 0}
                                onCreateTeam={handleCreateTeam}
                            />
                        </div>
                        <TeamTable
                            teams={teams}
                            onTeamClick={handleTeamClick}
                        />

                        {pagination && (
                            <PaginationControls
                                currentPage={currentPage + 1}
                                totalPages={pagination.totalPages}
                                pageSize={pagination.size}
                                pageSizeOptions={[10, 25, 50, 100]}
                                paginationInfo={{
                                    start: currentPage * pagination.size + 1,
                                    end: Math.min((currentPage + 1) * pagination.size, pagination.total),
                                    total: pagination.total
                                }}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                                rowsPerPageLabel={t('common.rowsPerPage')}
                                previousLabel={t('common.previous')}
                                nextLabel={t('common.next')}
                            />
                        )}
                    </div>
                </div>
            </Card>

            <TeamDetailsDialog
                open={teamDialogOpen}
                onOpenChange={handleCloseTeamDialog}
                selectedTeam={selectedTeamData || null}
                isLoading={isTeamLoading}
                error={teamError}
            />

            <CreateTeamDialog
                open={createTeamDialogOpen}
                onOpenChange={setCreateTeamDialogOpen}
            />
        </div>
    );
}
